import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Puzzle, CheckCircle2, XCircle, Save } from "lucide-react";

/** Iter122bk · ControlPanel · Studio Admin · Gestione moduli facoltativi.
 * Elenca `credius`, `sinistri`, `report`, `appalti` con toggle ON/OFF.
 * Alla creazione un nuovo studio ha tutti i moduli OFF (default): l'admin li attiva quando serve.
 */
export default function ModuliFacoltativiCard() {
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/tenant/moduli").then((r) => {
    setItems(r.data || []);
    setDirty(false);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = (key) => {
    setItems((s) => s.map((x) => x.key === key ? { ...x, attiva: !x.attiva } : x));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const abilitate = items.filter((x) => x.attiva).map((x) => x.key);
      await api.post("/tenant/moduli", { abilitate });
      toast.success("Moduli aggiornati · ricarica la pagina per applicare le modifiche al menu.");
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore aggiornamento moduli");
    }
    setSaving(false);
  };

  return (
    <Card className="p-4 border-2" data-testid="moduli-facoltativi-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center flex-shrink-0">
          <Puzzle className="w-5 h-5 text-brand" />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-bold text-slate-800">Moduli aggiuntivi</h3>
          <p className="text-xs text-slate-500 mt-0.5">Attiva o disattiva le funzionalità opzionali. I moduli disattivati non compaiono nel menu e non sono accessibili agli utenti dello studio.</p>
        </div>
      </div>
      <div className="space-y-2">
        {items.map((m) => (
          <div key={m.key} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid={`modulo-${m.key}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm text-slate-800">{m.label}</span>
                {m.attiva
                  ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5"><CheckCircle2 className="w-3 h-3" /> Attivo</span>
                  : <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5"><XCircle className="w-3 h-3" /> Disattivo</span>}
              </div>
              <p className="text-xs text-slate-500 mt-1">{m.descrizione}</p>
            </div>
            <Switch checked={m.attiva} onCheckedChange={() => toggle(m.key)} data-testid={`toggle-${m.key}`} />
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-slate-400 text-sm py-4">Caricamento…</div>}
      </div>
      {dirty && (
        <div className="mt-3 flex items-center justify-between gap-2 p-2 rounded-lg bg-amber-50 border border-amber-200">
          <span className="text-xs text-amber-800 font-semibold">Modifiche non salvate</span>
          <Button size="sm" onClick={save} disabled={saving} data-testid="moduli-save" className="bg-brand hover:bg-brand-dark h-8">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Salvataggio…" : "Salva"}
          </Button>
        </div>
      )}
    </Card>
  );
}
